import type { ReactNode } from 'react';
import type { EventSourceOptions, EventSourceConnection, EventSourceContextType } from 'src/types/event-source';

import { fetchEventSource } from '@microsoft/fetch-event-source';
import { useRef, useMemo, useState, useContext, useCallback, createContext } from 'react';

// ----------------------------------------------------------------------

const EventSourceContext = createContext<EventSourceContextType | null>(null);

// ----------------------------------------------------------------------

interface EventSourceProviderProps {
  children: ReactNode;
}

export function EventSourceProvider({ children }: EventSourceProviderProps) {
  const [connections, setConnections] = useState<Map<string, EventSourceConnection>>(new Map());
  const connectionsRef = useRef<Map<string, EventSourceConnection>>(new Map());
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());

  // Keep ref in sync with state
  connectionsRef.current = connections;

  const disconnect = useCallback((id: string) => {
    const abortController = abortControllersRef.current.get(id);
    if (abortController) {
      abortController.abort();
      abortControllersRef.current.delete(id);
    }

    setConnections(prev => {
      const newConnections = new Map(prev);
      newConnections.delete(id);
      return newConnections;
    });
  }, []);

  const connect = useCallback(async (id: string, url: string, options: EventSourceOptions = {}) => {
    // Disconnect existing connection if any
    if (connectionsRef.current.has(id)) {
      disconnect(id);
    }

    // Create abort controller for this connection
    const abortController = new AbortController();
    abortControllersRef.current.set(id, abortController);

    // Create initial connection state
    const connection: EventSourceConnection = {
      id,
      url,
      isConnected: false,
      isConnecting: true,
      options,
    };

    setConnections(prev => new Map(prev.set(id, connection)));

    try {
      await fetchEventSource(url, {
        ...options,
        signal: abortController.signal,
        onopen: async (response: Response) => {
          setConnections(prev => {
            const newConnections = new Map(prev);
            const existingConnection = newConnections.get(id);
            if (existingConnection) {
              newConnections.set(id, {
                ...existingConnection,
                isConnected: true,
                isConnecting: false,
                error: undefined,
              });
            }
            return newConnections;
          });
          options.onOpen?.(response);
        },
        onmessage: (event: any) => {
          setConnections(prev => {
            const newConnections = new Map(prev);
            const existingConnection = newConnections.get(id);
            if (existingConnection) {
              newConnections.set(id, {
                ...existingConnection,
                lastMessage: event,
                error: undefined,
              });
            }
            return newConnections;
          });
          options.onMessage?.(event);
        },
        onerror: (error: any) => {
          setConnections(prev => {
            const newConnections = new Map(prev);
            const existingConnection = newConnections.get(id);
            if (existingConnection) {
              newConnections.set(id, {
                ...existingConnection,
                isConnected: false,
                isConnecting: false,
                error,
              });
            }
            return newConnections;
          });
          options.onError?.(error);
        },
        onclose: () => {
          setConnections(prev => {
            const newConnections = new Map(prev);
            const existingConnection = newConnections.get(id);
            if (existingConnection) {
              newConnections.set(id, {
                ...existingConnection,
                isConnected: false,
                isConnecting: false,
              });
            }
            return newConnections;
          });
          options.onClose?.();
        },
      });
    } catch (error) {
      setConnections(prev => {
        const newConnections = new Map(prev);
        const existingConnection = newConnections.get(id);
        if (existingConnection) {
          newConnections.set(id, {
            ...existingConnection,
            isConnected: false,
            isConnecting: false,
            error: error instanceof Error ? error : new Error(String(error)),
          });
        }
        return newConnections;
      });
    } finally {
      // Clean up abort controller
      abortControllersRef.current.delete(id);
    }
  }, [disconnect]);

  const disconnectAll = useCallback(() => {
    // Abort all connections
    abortControllersRef.current.forEach((controller) => {
      controller.abort();
    });
    abortControllersRef.current.clear();

    // Clear all connections
    setConnections(new Map());
  }, []);

  const getConnection = useCallback((id: string) => connectionsRef.current.get(id), []);

  const isConnected = useCallback((id: string) => connectionsRef.current.get(id)?.isConnected ?? false, []);

  const isConnecting = useCallback((id: string) => connectionsRef.current.get(id)?.isConnecting ?? false, []);

  const getLastMessage = useCallback((id: string) => connectionsRef.current.get(id)?.lastMessage, []);

  const getError = useCallback((id: string) => connectionsRef.current.get(id)?.error, []);

  const value = useMemo<EventSourceContextType>(() => ({
    connections,
    connect,
    disconnect,
    disconnectAll,
    getConnection,
    isConnected,
    isConnecting,
    getLastMessage,
    getError,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [
    // Only include stable references - connections Map changes frequently
    connect,
    disconnect,
    disconnectAll,
    getConnection,
    isConnected,
    isConnecting,
    getLastMessage,
    getError,
  ]);

  return (
    <EventSourceContext.Provider value={value}>
      {children}
    </EventSourceContext.Provider>
  );
}

// ----------------------------------------------------------------------

export function useEventSource() {
  const context = useContext(EventSourceContext);
  if (!context) {
    throw new Error('useEventSource must be used within an EventSourceProvider');
  }
  return context;
}
